import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { healthFromRestV1 } from 'domain/mappers/healthFromRestV1';
import type { HealthStatusFE } from 'domain/system';
import { client } from 'graphql/client';
import { getSystemVersion } from 'graphql/queries';
import { validateSession } from 'store/tokenHandling/validateSession';

import { errorHandler } from './errors';
import { correlationHeaders, monitoringApi } from './restClient';

import type { SystemVersion } from 'API';
import { SystemVersionSchema } from 'APIzod';

const getHealthStatusFn = async (): Promise<{ data: HealthStatusFE }> => {
    try {
        const { status } = await monitoringApi.get<unknown>('/api/v1/health/status', {
            ...correlationHeaders(),
            // Resolve instead of rejecting on non-2xx, so the status code can be mapped directly.
            validateStatus: () => true,
            transformResponse: [],
        });

        // The session watchdog runs whenever the service answered, healthy or not — matching the
        // superseded GraphQL path, where an unhealthy system was still a 200. A transport failure
        // reached no response and skips it, exactly as the thrown GraphQL call did.
        await validateSession();

        return { data: healthFromRestV1(status) };
    } catch {
        // Silent, like the backend resolver: at a 5s poll a log line per failure would flood
        // Datadog for the whole outage, and `SystemHealthNotification` is the user-facing signal.
        return { data: healthFromRestV1(undefined) };
    }
};

// Stays on GraphQL: the resolver reads an aggregated version YAML from S3 with credentials the
// browser must not hold. `/monitoring-service/api/v1/version` is the monitoring service's own
// version, not the aggregated system version, so it is not a substitute.
const getSystemVersionFn = async () => {
    try {
        const { data } = await client.graphql({ query: getSystemVersion });
        return { data: SystemVersionSchema().parse(data.getSystemVersion) };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while getting the system api version', error);
    }
};

export const systemApi = createApi({
    reducerPath: 'system',
    // Still GraphQL: `getSystemVersion` runs `client.graphql`, so unlike `hubs.ts` this slice
    // cannot drop to `fakeBaseQuery`. Same mixed shape as `vehiclesV2.ts`.
    baseQuery: graphqlRequestBaseQuery({
        url: '/graphql',
    }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        // The `string` arg is the cache key, not an input — `'GetHealthStatus'`, unchanged from
        // the GraphQL endpoint. Do not "clean up" the unread argument.
        getHealthStatus: builder.query<HealthStatusFE, string>({
            queryFn: getHealthStatusFn,
        }),
        getSystemVersion: builder.query<SystemVersion, string>({
            queryFn: getSystemVersionFn,
        }),
    }),
});

export const { useGetHealthStatusQuery, useLazyGetSystemVersionQuery } = systemApi;
